import { ReactNode, useEffect, useState } from 'react';

import createSafeContext from '#/utils/createSafeContext';
import { createParadexWebSocket, ParadexWebSocket } from '#/api/ws/ws';
import useEffectOnce from '#/utils/useEffectOnce';
import { useWalletView } from '#/features/wallet/wallet-context';
import usePrevious from '#/utils/usePrevious';

interface ParadexWebSocketStatus {
  readonly error: string;
  readonly connected: boolean;
}

const INITIAL_WS_STATUS: ParadexWebSocketStatus = {
  error: '',
  connected: false,
};

export const [useParadexWebSocket, WSCtx] = createSafeContext<
  ParadexWebSocket | undefined
>('ParadexWebSocket');
export const [useParadexWebSocketStatus, WSStatusCtx] =
  createSafeContext<ParadexWebSocketStatus>('ParadexWebSocket');

interface Props {
  readonly children: ReactNode;
}

export function ParadexWebSocketProvider({ children }: Props) {
  const ws = useCreateParadexWebSocket();
  const [status, setStatus] = useState(INITIAL_WS_STATUS);

  useEffect(() => {
    if (ws == null) return;

    const handleOpen = () => {
      setStatus({ error: '', connected: true });
    };

    const handleClose = () => {
      setStatus({
        error: 'Disconnected from Paradex servers. Trying to reconnect...',
        connected: false,
      });
    };

    const handleError = () => {
      setStatus({
        error: 'Error connecting to Paradex servers. Trying to reconnect...',
        connected: false,
      });
    };

    ws.addEventListener('open', handleOpen);
    ws.addEventListener('close', handleClose);
    ws.addEventListener('error', handleError);

    return () => {
      ws.removeEventListener('open', handleOpen);
      ws.removeEventListener('close', handleClose);
      ws.removeEventListener('error', handleError);
    };
  }, [ws]);

  // Close connection on unmount
  useEffectOnce(() => () => ws?.close(1000));

  return (
    <WSStatusCtx value={status}>
      <WSCtx value={ws}>{children}</WSCtx>
    </WSStatusCtx>
  );
}

export function useCreateParadexWebSocket() {
  const [ws, setWs] = useState<ParadexWebSocket>();
  const { ethereumAddress } = useWalletView();
  const prevEthereumAddress = usePrevious(ethereumAddress);

  // Establish WS connection as App loads
  useEffectOnce(() => {
    setWs(createParadexWebSocket());
  });

  // Ensure we're un-authenticating WS connection
  // when user logs Out then Logs In
  // or when user instantly changes account
  useEffect(
    () => {
      const isUserLogsOut = prevEthereumAddress != null;
      if (isUserLogsOut) {
        ws?.close(1000);
        setWs(createParadexWebSocket());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ethereumAddress],
  );

  return ws;
}
